import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import axios from "axios";

const ALT_DATA_NEWS_ITEM_ID = "252"; // legacy: findNewsByCategoryItemId(252L)

export default function IndustryNews({
                                           contextPath = "",
                                           mode: modeProp,
                                           initialMode,
                                           initialQuickguideId,
                                           styleClasses = {},
                                       }) {
    const token = localStorage.getItem("token");
    const headers = { Authorization: `Bearer ${token}` };

    const urlParams = useMemo(() => new URLSearchParams(window.location.search), []);
    const urlMode = urlParams.get("mode") || undefined;
    const urlCategoryID = urlParams.get("categoryID") || undefined;
    const urlHot = urlParams.get("hot") || undefined;
    const urlNewsKeyword = urlParams.get("newsKeyword") || undefined; // legacy
    const urlQuickguideId = urlParams.get("quickguideId") || undefined;

    // ----- State -----
    const resolvedMode = (urlMode || modeProp || initialMode || "profiler").toLowerCase();
    const isAltDataMode = resolvedMode === "altdata";

    const [quickguideId] = useState(urlQuickguideId || initialQuickguideId || "");
    const [categories, setCategories] = useState([]); // { id, item }
    const [categoryId, setCategoryId] = useState(() => {
        if (urlCategoryID != null) return String(urlCategoryID);
        return isAltDataMode ? ALT_DATA_NEWS_ITEM_ID : "0";
    });
    const initialHot = urlHot ?? urlNewsKeyword ?? "";
    const hotRef = useRef(null);
    const [html, setHtml] = useState("");
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState(null);
    const [currentPage, setCurrentPage] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [totalCount, setTotalCount] = useState(0);
    const PAGE_SIZE = 50;

    // Renders big server HTML without re-rendering on every keystroke
    const NewsHtmlPane = React.memo(function NewsHtmlPane({ html, className }) {
    const memoHtml = useMemo(
        () => ({ __html: html || "" }),
        [html]
          );
          return <div className={className} dangerouslySetInnerHTML={memoHtml} />;
        });

    // Axios
    const axiosClient = useMemo(() => {
        const instance = axios.create({
            baseURL: contextPath || "",
            headers: { Accept: "text/html" },
        });
        const token = localStorage.getItem("token");
        if (token) instance.defaults.headers.common["Authorization"] = `Bearer ${token}`;
        return instance;
    }, [contextPath]);

    const API_ROUTES = useMemo(
        () => ({
            categories: "/news/categories",
            list: "/news",
        }),
        []
    );


    const selectedCategoryName = useMemo(() => {
        if (categoryId === "-1") return "---- Select News Category ----";
        if (categoryId === "0") return "All News";
        const match = categories.find((c) => String(c.id) === String(categoryId));
        return match ? match.item : "All News";
    }, [categoryId, categories]);

    const loadCategories = useCallback(async () => {
        if (isAltDataMode) return;
        try {
            const { data } = await axios.get(`${contextPath}${API_ROUTES.categories}`, { headers });
            setCategories(Array.isArray(data) ? data : []);
        } catch (e) {
            console.warn("Failed to load news categories", e);
        }
    }, [contextPath, API_ROUTES, isAltDataMode]);

    const fetchNews = useCallback(
        async ({ forceDefaults = false, nextCategoryId, nextHot, page = 0 } = {}) => {
            setLoading(true);
            setErr(null);

            try {
                const params = new URLSearchParams();

                const hotToUse = (nextHot ?? hotRef.current?.value ?? "").trim();

                if (isAltDataMode) {
                    const catIdToUse = String(nextCategoryId ?? categoryId ?? ALT_DATA_NEWS_ITEM_ID);
                    params.set("categoryID", catIdToUse);
                    if (hotToUse) params.set("hot", hotToUse);
                } else {
                    if (!forceDefaults) {
                        const catIdToUse = nextCategoryId ?? categoryId;
                        if (hotToUse) params.set("hot", hotToUse);
                        if (
                            catIdToUse != null &&
                            catIdToUse !== "" &&
                            catIdToUse !== "-1" &&
                            catIdToUse !== "0"
                        ) {
                            params.set("categoryID", String(catIdToUse));
                        }
                    }
                }

                if (quickguideId) params.set("quickguideId", String(quickguideId));
                params.set("mode", resolvedMode);
                params.set("page", String(page));
                params.set("size", String(PAGE_SIZE));

                const url = `${API_ROUTES.list}${params.toString() ? `?${params.toString()}` : ""}`;
                const response = await axiosClient.get(url, { responseType: "text" });
                setHtml(typeof response.data === "string" ? response.data : String(response.data ?? ""));

                const tp = parseInt(response.headers["x-total-pages"] || "0", 10);
                const cp = parseInt(response.headers["x-current-page"] || "0", 10);
                const tc = parseInt(response.headers["x-total-count"] || "0", 10);
                setTotalPages(tp);
                setCurrentPage(cp);
                setTotalCount(tc);
            } catch (e) {
                setErr(e?.response?.data?.message || e.message || "Failed to load news.");
                setHtml("");
            } finally {
                setLoading(false);
            }
        },
        [axiosClient, API_ROUTES, categoryId, quickguideId, resolvedMode, isAltDataMode]
    );

    const handleSearch = useCallback(
        (e) => {
            e?.preventDefault?.();
            fetchNews({ nextHot: hotRef.current?.value || "", page: 0 });
        },
        [fetchNews]
    );

    const handleClear = useCallback(async () => {
        if (hotRef.current) hotRef.current.value = "";
        setCategoryId(isAltDataMode ? ALT_DATA_NEWS_ITEM_ID : "0");
        await fetchNews({ forceDefaults: true, page: 0 });
    }, [fetchNews, isAltDataMode]);

    const handleCategoryChange = useCallback(
        async (e) => {
            const value = e.target.value;
            setCategoryId(value);
            await fetchNews({ nextCategoryId: value, page: 0 });
        },
        [fetchNews]
    );

    const handlePageChange = useCallback(
        (newPage) => {
            fetchNews({ page: newPage });
        },
        [fetchNews]
    );

    useEffect(() => {
        document.title = "3D Innovations Profiler - News Archive";
    }, []);

    useEffect(() => {
        loadCategories();
    }, [loadCategories]);

    useEffect(() => {
        fetchNews();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // run once

    // Styles
    const cx = {
        root: styleClasses.root ?? "p-4",
        toolbar:
            styleClasses.toolbar ??
            "w-full flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between",
        select:
            styleClasses.select ??
            "border rounded px-2 py-1 text-sm min-w-[240px] outline-none focus:ring",
        input:
            styleClasses.input ??
            "border rounded px-2 py-1 text-sm min-w-[240px] outline-none focus:ring",
        button:
            styleClasses.button ??
            "inline-flex items-center justify-center border rounded px-3 py-1 text-sm hover:bg-gray-50 active:bg-gray-100",
        list: styleClasses.list ?? "mt-3 border rounded p-3 h-[575px] overflow-auto",
        heading: styleClasses.heading ?? "textHeadings text-lg font-semibold",
    };

    return (
        <div className="max-w-5xl mx-auto flex flex-col px-4 pt-2 pb-1 flex-1 min-h-0 overflow-hidden">
            <h1 className="text-xl font-semibold text-[#25408f] mb-2 shrink-0">
                Industry News Archive:
            </h1>

            <div className="mb-2 flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between shrink-0">
                <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                    {!isAltDataMode && (
                        <div className="relative">
                            <select
                                id="Categorys"
                                name="Categorys"
                                value={categoryId}
                                onChange={handleCategoryChange}
                                className="appearance-none rounded-lg border border-gray-300 bg-white px-3 py-2 pr-9 text-sm shadow-sm outline-none transition focus:border-[#25408f] focus:ring-2 focus:ring-[#25408f]/30"
                            >
                                <option value="-1">---- Select News Category ----</option>
                                <option value="0">All News</option>
                                {categories.map((c) => (
                                    <option key={c.id} value={String(c.id)} id={c.item}>
                                        {c.item}
                                    </option>
                                ))}
                            </select>
                            <svg
                                className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
                                viewBox="0 0 20 20"
                                fill="currentColor"
                                aria-hidden="true"
                            >
                                <path d="M5.5 7.5l4.5 4.5 4.5-4.5" />
                            </svg>
                        </div>
                    )}

                    <div className="flex items-center gap-2">
                        <input
                            ref={hotRef}
                            type="text"
                            id="hot"
                            name="hot"
                            placeholder="Keyword search"
                            defaultValue={initialHot}
                            className="w-64 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm shadow-sm outline-none transition focus:border-[#25408f] focus:ring-2 focus:ring-[#25408f]/30"
                        />

                        <button
                            onClick={handleSearch}
                            className="rounded-lg bg-[#25408f] px-4 py-2 text-sm font-medium text-white shadow-sm transition hover:bg-[#1e377a] focus:outline-none focus:ring-2 focus:ring-[#25408f]/40"
                        >
                            Search
                        </button>

                        <button
                            onClick={handleClear}
                            className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm transition hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-300/40"
                        >
                            Clear
                        </button>
                    </div>
                </div>
            </div>

            <div className="news-pane relative flex-1 min-h-0 overflow-auto rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
              {loading ? (
                    <div className="absolute inset-0 flex items-center justify-center">
                          <svg
                            className="h-8 w-8 animate-spin text-[#25408f]"
                            viewBox="0 0 24 24"
                            aria-label="Loading"
                          >
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                          </svg>
                </div>
              ) : html && html.trim().length > 0 ? (
                <NewsHtmlPane className="" html={html} />
              ) : (
                <ul>
                  <li className="text-sm text-gray-600">No news found.</li>
               </ul>
              )}
            </div>

            {totalPages > 1 && !loading && (
                <div className="mt-1 flex items-center justify-between shrink-0 py-1">
                    <span className="text-sm text-gray-600">
                        {totalCount.toLocaleString()} results
                    </span>
                    <div className="flex items-center gap-1">
                        <button
                            onClick={() => handlePageChange(0)}
                            disabled={currentPage === 0}
                            className="rounded border border-gray-300 px-2 py-1 text-sm disabled:opacity-40 hover:bg-gray-50"
                        >
                            First
                        </button>
                        <button
                            onClick={() => handlePageChange(currentPage - 1)}
                            disabled={currentPage === 0}
                            className="rounded border border-gray-300 px-2 py-1 text-sm disabled:opacity-40 hover:bg-gray-50"
                        >
                            Prev
                        </button>
                        <span className="px-3 text-sm text-gray-700">
                            Page {currentPage + 1} of {totalPages}
                        </span>
                        <button
                            onClick={() => handlePageChange(currentPage + 1)}
                            disabled={currentPage >= totalPages - 1}
                            className="rounded border border-gray-300 px-2 py-1 text-sm disabled:opacity-40 hover:bg-gray-50"
                        >
                            Next
                        </button>
                        <button
                            onClick={() => handlePageChange(totalPages - 1)}
                            disabled={currentPage >= totalPages - 1}
                            className="rounded border border-gray-300 px-2 py-1 text-sm disabled:opacity-40 hover:bg-gray-50"
                        >
                            Last
                        </button>
                    </div>
                </div>
            )}

            {err && (
                <div className="mt-2 rounded border border-red-200 bg-red-50 p-2 text-sm text-red-700">
                    {err}
                </div>
            )}
        </div>
    );

}
